import { EASE_IN_OUT } from './easing';
import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useScroll, useSpring, useTransform } from 'framer-motion';
import { watchDoc } from '../../firestoreLive';
import { PERSONAL_INFO, SHORT_BIO, SKILLS } from '../../constants';
import { ProfileData, SkillsData } from '../../types';
import { RevealLines, FadeIn } from './RevealText';
import VelocityMarquee from '../VelocityMarquee';
import MarqueeWord from './MarqueeWord';
import VelocitySkew from './VelocitySkew';
import { useTouchActive } from './useTouchActive';

// fallback until the Firestore skills doc loads — the marquee shows the real,
// admin-managed skill set, same data the old site's Skills section used
const FALLBACK_TOOLS = SKILLS.flatMap((c) => c.items);

/**
 * Light editorial section: a big masked statement, supporting bio, portrait
 * with scroll parallax, and a velocity marquee of the toolkit.
 */
const AboutSection: React.FC = () => {
  const [profile, setProfile] = useState<ProfileData>({ ...PERSONAL_INFO, bio: SHORT_BIO, socials: [] });
  const [tools, setTools] = useState<string[]>(FALLBACK_TOOLS);

  // the marquee is driven by the same content/skills doc the admin edits
  useEffect(() => {
    const unsub = watchDoc<SkillsData>('content', 'skills', (d) => {
      const items = (d.categories || []).flatMap((c) => c.items).filter(Boolean);
      if (items.length) setTools(items);
    });
    return () => unsub();
  }, []);
  const imgWrapRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: imgWrapRef, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);

  // cursor tilt plus counter-drift of the image, same feel as the work cards
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const spring = { stiffness: 170, damping: 20, mass: 0.4 };
  const prx = useSpring(rx, spring);
  const pry = useSpring(ry, spring);
  const ppx = useSpring(px, spring);
  const ppy = useSpring(py, spring);

  const onPortraitMove = (e: React.MouseEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top) / r.height - 0.5;
    ry.set(nx * 7);
    rx.set(-ny * 6);
    px.set(-nx * 14);
    py.set(-ny * 14);
  };

  // No hover on a phone, so the portrait zoom would never happen. It runs when
  // the portrait reaches the reading band, like the work cards.
  const [portraitHover, setPortraitHover] = useState(false);
  const portraitRef = useRef<HTMLDivElement>(null);
  const portraitActive = useTouchActive(portraitRef, '-38% 0px -38% 0px');
  const portraitLit = portraitHover || portraitActive;

  const resetPortrait = () => {
    rx.set(0);
    ry.set(0);
    px.set(0);
    py.set(0);
  };

  useEffect(() => {
    const unsub = watchDoc<ProfileData>('content', 'profile', (d) => {
      setProfile((prev) => ({ ...prev, ...d }));
    });
    return () => unsub();
  }, []);

  return (
    <section id="about" className="relative py-section">
      <div className="max-w-[92rem] mx-auto px-5 md:px-10">
        {/* Statement */}
        <h2 className="mb-16 md:mb-28">
          <RevealLines
            lines={['Ten years of making', 'pixels move, from stadium', 'screens to XR stages.']}
            lineClassName="font-grotesk font-semibold text-[9vw] md:text-[4.6vw] leading-[1.04] tracking-tight text-ink"
          />
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 items-start">
          {/* Portrait: smaller, and interactive like the project cards */}
          <VelocitySkew className="md:col-span-4 md:col-start-1">
            <div className="group max-w-[300px] md:max-w-none" style={{ perspective: 1000 }}>
              {/* Tilt and clip are split for the same reason as the work cards:
                  WebKit will not clip to a border-radius on a box that is
                  itself 3D-transformed, which showed up as square corners. */}
              <motion.div
                onMouseMove={onPortraitMove}
                onMouseEnter={() => setPortraitHover(true)}
                onMouseLeave={() => {
                  setPortraitHover(false);
                  resetPortrait();
                }}
                style={{ rotateX: prx, rotateY: pry }}
                className="will-change-transform"
              >
              <div
                ref={portraitRef}
                style={{ WebkitMaskImage: '-webkit-radial-gradient(white, black)' }}
                className="relative aspect-[4/5] rounded-[20px] md:rounded-[28px] overflow-hidden bg-haze"
              >
                <motion.div
                  className="absolute inset-0"
                  initial={{ clipPath: 'inset(100% 0 0 0)' }}
                  whileInView={{ clipPath: 'inset(0% 0 0 0)' }}
                  viewport={{ once: true, margin: '-12% 0px' }}
                  transition={{ duration: 1.1, ease: EASE_IN_OUT }}
                >
                  <motion.div ref={imgWrapRef} className="absolute -inset-[6%]" style={{ x: ppx, y: ppy }}>
                    <motion.img
                      src={profile.avatarUrl}
                      alt={profile.name}
                      style={{ y: imgY }}
                      animate={{ scale: portraitLit ? 1.11 : 1.05 }}
                      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute inset-0 w-full h-full object-cover" 
                      loading="lazy"
                      draggable={false}
                    />
                    {/* The second portrait, if there is one, sits directly on
                        top and takes the same scale, so only its opacity
                        moves. It carries no alt text: it is the same person as
                        the image beneath it, and announcing him twice tells a
                        screen reader nothing it did not already know. */}
                    {profile.avatarHoverUrl && (
                      <motion.img
                        src={profile.avatarHoverUrl}
                        alt=""
                        aria-hidden="true"
                        style={{ y: imgY }}
                        animate={{ scale: portraitLit ? 1.11 : 1.05, opacity: portraitLit ? 1 : 0 }}
                        transition={{
                          scale: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
                          opacity: { duration: 0.45, ease: 'easeOut' },
                        }}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                        draggable={false}
                      />
                    )}
                  </motion.div>
                </motion.div>
              </div>
              </motion.div>
            </div>
          </VelocitySkew>

          {/* Bio */}
          <VelocitySkew className="md:col-span-6 md:col-start-7 flex flex-col gap-8 md:pt-4">
            <FadeIn>
              <p className="font-grotesk text-xl md:text-2xl leading-relaxed text-ink/85">
                {profile.bio}
              </p>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="grid grid-cols-2 gap-y-5 gap-x-8 pt-6 border-t border-ink/10">
                {[
                  ['Focus', 'Motion · 3D · XR'],
                  ['Based in', 'Los Angeles, CA'],
                  ['Experience', '10+ years'],
                  ['Availability', 'Freelance & contract'],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="font-grotesk text-[10px] uppercase tracking-[0.25em] text-ink/65 mb-1">{k}</p>
                    <p className="font-grotesk text-sm md:text-base text-ink font-medium">{v}</p>
                  </div>
                ))}
              </div>
            </FadeIn>
          </VelocitySkew>
        </div>
      </div>

      {/* Toolkit marquee */}
      <div className="mt-20 md:mt-32 border-y border-ink/10 py-5 md:py-7">
        <VelocityMarquee speed={62} className="select-none">
          {tools.map((t) => (
            <MarqueeWord key={t} tone="ink" size="text-2xl md:text-4xl">
              {t}
            </MarqueeWord>
          ))}
        </VelocityMarquee>
      </div>
    </section>
  );
};

export default AboutSection;
